/**
 * Terrain patch geometry generation — pure data in, typed arrays out.
 * Runs inside the terrain worker. Positions are emitted relative to the
 * patch center so float32 vertex buffers stay precise on multi-km planets.
 *
 * Layout: res*res grid vertices, followed by (4*res-4) skirt vertices that
 * duplicate the perimeter and sink toward the planet center to hide cracks
 * between adjacent LOD levels. Near sharp waterfall cliffs, optional indices
 * append terrain-sampled contour vertices and independent face normals.
 */
import {
  PlanetSampler,
  SettlementIndex,
  deserializeCityGraph,
  type CellId,
  type PlanetDef,
  type TerrainSample,
} from "@hyperlight/shared";
import { faceDir } from "./cubesphere";
import { buildGrassPatch, type GrassPatchData } from './flora/grassPatch';
import { buildWetlandData, type WetlandData } from './flora/wetlandData';
import { buildCoastalData, buildCoastalFarData, type CoastalData } from './flora/coastalData';
import { buildInlandWetlandData, buildInlandWetlandFarData, type InlandWetlandData } from './flora/inlandWetlandData';
import { buildSavannaData, buildSavannaFarData, type SavannaData } from './flora/savannaData';
import {buildInlandWetlandWaterData,type InlandWetlandWaterData} from './water/inlandWetlandWaterData';
import { buildRiparianData, type RiparianData } from './flora/riparianData';
import type { GrassTuning } from './flora/grassData';
import {refineWaterfallCliffMesh,waterfallCliffMeshNeeded,resolveWaterfallBankNormals} from './waterfallCliffMesh';

/**
 * Broadcast payload from the main thread's RegionService to every terrain worker
 * (W2b-2 Task 1). Carries the SERIALIZED CityGraph buffers of newly-materialized
 * regions plus the planet they belong to. A SettlementIndex is per-process (live
 * Maps + a sampler ref — not structured-cloneable), so the index itself is NEVER
 * posted; each worker rebuilds its own via {@link foldRegionBuffers}.
 *
 * DEVIATION FROM THE TASK-1 SKETCH (documented, deliberate): the brief sketched
 * `{ kind, seed, buffers }`. But rebuilding an index needs the SAMPLER (its
 * constructor takes one) and the sampler needs the full {@link PlanetDef}, not
 * just the seed string — so `foldRegionBuffers(seed, …)` could not stand up the
 * index on a fold-before-sampler. This message therefore carries the whole
 * `planet` (structured-cloneable, exactly as {@link PatchRequest} already does),
 * and `foldRegionBuffers(planet, …)` derives the seed from `planet.seed`.
 */
export interface RegionsMsg {
  kind: "regions";
  planet: PlanetDef;
  buffers: ArrayBuffer[];
}

/** Main-thread request to grow region graphs off-thread. */
export interface RegionBuildRequest {
  kind: "regionBuild";
  id: number;
  planet: PlanetDef;
  cells: CellId[];
}

/** Serialized graph buffers returned from an off-thread region build. */
export interface RegionBuildData {
  kind: "regionBuilt";
  id: number;
  buffers: ArrayBuffer[];
}

export interface PatchRequest {
  id: number;
  planet: PlanetDef;
  face: number;
  level: number;
  /** Cube-face corner coords in [-1, 1]; size is the patch edge length. */
  u0: number;
  v0: number;
  size: number;
  res: number;
  /** Only near flora tiles request grass, on the same settled terrain snapshot. */
  grass?: GrassTuning;
  wetland?: boolean;
  coastal?: boolean;
  coastalFar?: boolean;
  savanna?: boolean;
  savannaFar?: boolean;
  inlandWetland?: boolean;
  inlandWetlandFar?: boolean;
  inlandWater?: boolean;
  riparian?: boolean;
}

export interface PatchData {
  id: number;
  grass?: GrassPatchData;
  wetland?: WetlandData;
  coastal?: CoastalData;
  savanna?: SavannaData;
  inlandWetland?: InlandWetlandData;
  inlandWater?: InlandWetlandWaterData;
  riparian?: RiparianData;
  /** Only cliff-crossing tiles replace the shared regular-grid topology. */
  indices?: Uint32Array;
  cliffFaces?: number;
  surfaceIndexCount?: number;
  positions: Float32Array;
  normals: Float32Array;
  /**
   * Per-vertex biome fields, vec4 (relH, river, temp, moist). The color
   * DECISIONS happen per-fragment in the terrain shader, so water lines,
   * beaches, cliffs and snowlines resolve at pixel resolution instead of
   * zigzagging along triangle edges.
   */
  fields: Float32Array;
  /** Per-vertex up·normal (1 = flat) for the fragment cliff blend. */
  slopes: Float32Array;
  /**
   * Planet-local vertex positions (meters) — stable triplanar texture
   * coordinates that never swim when the floating origin rebases.
   */
  ppos: Float32Array;
  /** Per-vertex downstream flow tangent (planet-local), 0 off a river. */
  riverFlow: Float32Array;
  /** Per-vertex downstream arclength of the nearest river centreline point (m,
   *  0 off a river) — the CONTINUOUS flow-phase coordinate. The old
   *  dot(ppos, flow) shader seed reset at every centreline densify step, so
   *  ripples banded every ~20 m instead of scrolling downstream. */
  riverS: Float32Array;
  /** Per-vertex road-surface SDF (m, ±8; +8 = no road) — TerrainSample.road.
   *  Linear interpolation of an SDF stays a usable distance field, so the
   *  terrain shader paints a coherent road strip at EVERY LOD (W2d-3 wrap:
   *  roads are part of the ground, not floating ribbons). */
  roadSd: Float32Array;
  /** vec2: grounded ice thickness (m), glacial province weight. */
  glacierIce?: Float32Array;
  volcanism?: Float32Array;
  estuaryShelter?: Float32Array;
  /** Shared mobile-sand coverage; zero on exposed rock and settled/wet ground. */
  duneSand?: Float32Array;
  /** Exposed sandstone coverage, shared by buttes and canyon walls. The name
   * retains the existing worker contract; omitted when all vertices are zero. */
  butteRock?: Float32Array;
  /** Optional canyon rock / sediment-interior coverage, two floats per vertex. */
  canyonSurface?: Float32Array;
  /** Local inland habitat weight and saturated peat coverage; absent elsewhere. */
  wetlandSurface?: Float32Array;
  /** Dry riverbank sediment and exposed stone; absent on unaffected tiles. */
  riverBankSurface?: Float32Array;
  snowRetention?: Float32Array;
  /** Patch center in planet-local space (float64-accurate). */
  center: [number, number, number];
  /**
   * Tight bounding sphere of the EMITTED geometry (grid + skirt), in patch-local
   * space — i.e. relative to `center`, the same frame `positions` lives in.
   *
   * Must be measured, never estimated from the patch's cube-face span: on a cliff
   * the surface runs near-vertically, so vertices sit an ARBITRARY multiple of the
   * horizontal span away from the center (measured: 2.8× on a desert leaf patch).
   * A span-derived sphere doesn't contain them, and three.js then frustum-culls the
   * mesh while the cliff is still on screen — it despawns at the viewport edge.
   */
  boundCenter: [number, number, number];
  boundRadius: number;
}

/**
 * Tight bounding sphere of a packed xyz position buffer: box center, then the
 * max vertex distance from it. Same construction three.js uses in
 * `BufferGeometry.computeBoundingSphere`, run here in the worker so the main
 * thread pays nothing at patch-adopt time.
 */
function boundingSphere(
  positions: Float32Array,
): { center: [number, number, number]; radius: number } {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let o = 0; o < positions.length; o += 3) {
    const x = positions[o], y = positions[o + 1], z = positions[o + 2];
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2, cz = (minZ + maxZ) / 2;
  let maxSq = 0;
  for (let o = 0; o < positions.length; o += 3) {
    const dx = positions[o] - cx, dy = positions[o + 1] - cy, dz = positions[o + 2] - cz;
    const d = dx * dx + dy * dy + dz * dz;
    if (d > maxSq) maxSq = d;
  }
  return { center: [cx, cy, cz], radius: Math.sqrt(maxSq) };
}

/** Number of skirt vertices for a given grid resolution. */
export function skirtCount(res: number): number {
  return 4 * res - 4;
}

/**
 * Walk the grid perimeter counter-clockwise (seen from outside the planet).
 * Returns grid vertex indices in walk order.
 */
export function perimeterIndices(res: number): Uint32Array {
  const out = new Uint32Array(skirtCount(res));
  let k = 0;
  for (let i = 0; i < res; i++) out[k++] = i; // bottom row, +u
  for (let j = 1; j < res; j++) out[k++] = j * res + (res - 1); // right col, +v
  for (let i = res - 2; i >= 0; i--) out[k++] = (res - 1) * res + i; // top row, -u
  for (let j = res - 2; j >= 1; j--) out[k++] = j * res; // left col, -v
  return out;
}

/**
 * Shared index buffer for all patches of a given resolution (grid + skirt).
 * Built once on the main thread and reused by every patch geometry.
 */
export function buildPatchIndex(res: number): Uint32Array {
  const gridQuads = (res - 1) * (res - 1);
  const perim = perimeterIndices(res);
  const P = perim.length;
  const indices = new Uint32Array(gridQuads * 6 + P * 6);
  let k = 0;
  for (let j = 0; j < res - 1; j++) {
    for (let i = 0; i < res - 1; i++) {
      const a = j * res + i;
      const b = a + 1;
      const c = a + res;
      const d = c + 1;
      indices[k++] = a;
      indices[k++] = b;
      indices[k++] = d;
      indices[k++] = a;
      indices[k++] = d;
      indices[k++] = c;
    }
  }
  const skirtBase = res * res;
  for (let e = 0; e < P; e++) {
    const g0 = perim[e];
    const g1 = perim[(e + 1) % P];
    const s0 = skirtBase + e;
    const s1 = skirtBase + ((e + 1) % P);
    indices[k++] = g0;
    indices[k++] = s0;
    indices[k++] = s1;
    indices[k++] = g0;
    indices[k++] = s1;
    indices[k++] = g1;
  }
  return indices;
}

const cliffGridIndices = new Map<number,Uint32Array>();

/** Sampler cache — planets are immutable, keyed by seed. */
const samplers = new Map<string, PlanetSampler>();

/**
 * Per-seed settlement carve index cache — the worker-side sibling of `samplers`
 * (same 32-cap FIFO eviction). Regions broadcast via {@link RegionsMsg} are
 * folded in here by {@link foldRegionBuffers}; the index for a seed is attached
 * to that seed's cached sampler so {@link buildPatch} meshes carved terrain.
 */
// DESYNC MODE (benign): samplers + regionIndices evict on independent 32-cap FIFOs, so a
// seed's index can be evicted while its sampler survives — a later fold then starts a FRESH
// index, dropping previously-folded regions (missing carve until the main thread re-broadcasts
// them). Because attach is seed-KEYED it can never MIS-attach one seed's index to another; the
// worst case is a transient missing carve, self-healed by the next residency broadcast.
const regionIndices = new Map<string, SettlementIndex>();

export function getSampler(def: PlanetDef): PlanetSampler {
  let s = samplers.get(def.seed);
  if (!s) {
    s = new PlanetSampler(def);
    samplers.set(def.seed, s);
    if (samplers.size > 32) {
      const first = samplers.keys().next().value as string;
      samplers.delete(first);
    }
    // Order-independent wiring, half A: if this seed's index was already built
    // (regions folded before the sampler existed, or the sampler was evicted and
    // is being rebuilt), attach it on creation so the very first patch is carved.
    const idx = regionIndices.get(def.seed);
    if (idx) s.setSettlementIndex(idx);
  }
  return s;
}

/**
 * Fold broadcast region buffers into this process's per-seed SettlementIndex and
 * wire it onto the seed's cached sampler (W2b-2 Task 1). Pure worker-side handler
 * — the terrain worker's "regions" branch and the node oracle both call it.
 *
 * The index is created lazily (keyed by `planet.seed`, mirroring `samplers`) with
 * the SAME sampler instance {@link buildPatch} uses, then each buffer is
 * deserialized and `addRegion`-ed. Because `addRegion` is idempotent and
 * region-order-independent, re-broadcasting a region (or folding across workers in
 * any order) converges on a byte-identical index — so this is safe to call
 * repeatedly. Order-independent wiring, half B: attaching to an ALREADY-cached
 * sampler (getSampler's half A covers the fold-before-sampler direction).
 */
export function foldRegionBuffers(planet: PlanetDef, buffers: ArrayBuffer[]): void {
  const sampler = getSampler(planet);
  let idx = regionIndices.get(planet.seed);
  if (idx === undefined) {
    idx = new SettlementIndex(sampler);
    regionIndices.set(planet.seed, idx);
    if (regionIndices.size > 32) {
      const first = regionIndices.keys().next().value as string;
      regionIndices.delete(first);
    }
    sampler.setSettlementIndex(idx);
  }
  for (const b of buffers) idx.addRegion(deserializeCityGraph(b));
}

export function buildPatch(req: PatchRequest): PatchData {
  const { planet, face, u0, v0, size, res } = req;
  const sampler = getSampler(planet);
  const radius = planet.radius;
  const amplitude = Math.max(sampler.amplitude, 1e-6);

  const step = size / (res - 1);
  const ext = res + 2; // sampled grid includes a 1-cell border for normals

  // Sample the extended grid (float64 positions + biome fields per point).
  const pts = new Float64Array(ext * ext * 3);
  const temps = new Float32Array(ext * ext);
  const moists = new Float32Array(ext * ext);
  const rivers = new Float32Array(ext * ext);
  const roads = new Float32Array(ext * ext); // road-surface SDF per sample
  const ice = new Float32Array(ext * ext);
  const glaciation = new Float32Array(ext * ext);
  const volcanic = new Float32Array(ext * ext);
  const shelter = new Float32Array(ext * ext);
  const sand = new Float32Array(ext * ext);
  let butte:Float32Array|undefined;
  let canyon:Float32Array|undefined;
  let inland:Float32Array|undefined;
  let bank:Float32Array|undefined;
  let cold:Float32Array|undefined;
  const flows = new Float32Array(ext * ext * 3); // downstream tangent per sample
  const riverArcs = new Float32Array(ext * ext); // downstream arclength per sample
  const dir: number[] = [0, 0, 0];
  faceDir(face, u0 + size / 2, v0 + size / 2, dir);
  const resolveCliff=waterfallCliffMeshNeeded(sampler,dir,size*radius,res);
  const ts: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  for (let j = 0; j < ext; j++) {
    const v = v0 + (j - 1) * step;
    for (let i = 0; i < ext; i++) {
      const u = u0 + (i - 1) * step;
      faceDir(face, u, v, dir);
      sampler.sample(dir[0], dir[1], dir[2], ts, true);
      const r = radius + ts.h * amplitude;
      const k = j * ext + i;
      const o = k * 3;
      pts[o] = dir[0] * r;
      pts[o + 1] = dir[1] * r;
      pts[o + 2] = dir[2] * r;
      temps[k] = ts.temp;
      moists[k] = ts.moist;
      rivers[k] = ts.river;
      roads[k] = ts.road;
      ice[k] = ts.glacierIceM ?? 0;
      glaciation[k] = ts.glaciation ?? 0;
      volcanic[k] = ts.volcanism ?? 0;
      shelter[k] = ts.estuaryShelter ?? 0;
      sand[k] = ts.duneSand ?? 0;
      const sediment=Math.max(ts.butteRock??0,ts.canyonRock??0);
      if(sediment>0){butte??=new Float32Array(ext*ext);butte[k]=sediment;}
      const canyonRock=ts.canyonRock??0,canyonGround=ts.canyonGround??0;
      if(canyonRock>0||canyonGround>0){canyon??=new Float32Array(ext*ext*2);canyon[k*2]=canyonRock;canyon[k*2+1]=canyonGround;}
      if((ts.wetlandMask??0)>0){inland??=new Float32Array(ext*ext*2);inland[k*2]=ts.wetlandMask!;inland[k*2+1]=ts.wetlandMud??0;}
      if((ts.snowRetention??-1)>=0){cold??=new Float32Array(ext*ext).fill(-1);cold[k]=ts.snowRetention!;}
      if((ts.riverBankSediment??0)>0||(ts.riverBankRock??0)>0){bank??=new Float32Array(ext*ext*2);bank[k*2]=ts.riverBankSediment??0;bank[k*2+1]=ts.riverBankRock??0;}
      flows[k * 3] = ts.flowx;
      flows[k * 3 + 1] = ts.flowy;
      flows[k * 3 + 2] = ts.flowz;
      riverArcs[k] = ts.riverS ?? 0;
    }
  }

  // Patch center: surface point at the patch midpoint.
  faceDir(face, u0 + size / 2, v0 + size / 2, dir);
  const cr = radius + sampler.height(dir[0], dir[1], dir[2]);
  const center: [number, number, number] = [dir[0] * cr, dir[1] * cr, dir[2] * cr];

  const skirtN = skirtCount(res);
  const vertCount = res * res + skirtN;
  const positions = new Float32Array(vertCount * 3);
  const normals = new Float32Array(vertCount * 3);
  const fields = new Float32Array(vertCount * 4);
  const slopes = new Float32Array(vertCount);
  const ppos = new Float32Array(vertCount * 3);
  const riverFlow = new Float32Array(vertCount * 3);
  const riverS = new Float32Array(vertCount);
  const roadSd = new Float32Array(vertCount);
  const glacierIce = new Float32Array(vertCount * 2);
  const volcanism = new Float32Array(vertCount);
  const estuaryShelter = new Float32Array(vertCount);
  const duneSand = new Float32Array(vertCount);
  let butteRock:Float32Array|undefined;
  let canyonSurface:Float32Array|undefined;
  let wetlandSurface:Float32Array|undefined;
  let riverBankSurface:Float32Array|undefined;
  const snowRetention=cold?new Float32Array(vertCount).fill(-1):undefined;
  let minHeight=Infinity,maxHeight=-Infinity,maxHeightStep=0;

  for (let j = 0; j < res; j++) {
    for (let i = 0; i < res; i++) {
      const k = (j + 1) * ext + (i + 1); // extended-grid index
      const e = k * 3;
      const gi = j * res + i;
      const o = gi * 3;
      const px = pts[e], py = pts[e + 1], pz = pts[e + 2];
      positions[o] = px - center[0];
      positions[o + 1] = py - center[1];
      positions[o + 2] = pz - center[2];
      ppos[o] = px;
      ppos[o + 1] = py;
      ppos[o + 2] = pz;

      // Central-difference normal from neighbors in the extended grid.
      const exm = ((j + 1) * ext + i) * 3;
      const exp_ = ((j + 1) * ext + (i + 2)) * 3;
      const eym = (j * ext + (i + 1)) * 3;
      const eyp = ((j + 2) * ext + (i + 1)) * 3;
      const dux = pts[exp_] - pts[exm], duy = pts[exp_ + 1] - pts[exm + 1], duz = pts[exp_ + 2] - pts[exm + 2];
      const dvx = pts[eyp] - pts[eym], dvy = pts[eyp + 1] - pts[eym + 1], dvz = pts[eyp + 2] - pts[eym + 2];
      let nx = duy * dvz - duz * dvy;
      let ny = duz * dvx - dux * dvz;
      let nz = dux * dvy - duy * dvx;
      const nl = 1 / Math.sqrt(nx * nx + ny * ny + nz * nz);
      nx *= nl; ny *= nl; nz *= nl;
      normals[o] = nx;
      normals[o + 1] = ny;
      normals[o + 2] = nz;

      const pr = Math.sqrt(px * px + py * py + pz * pz);
      const height=pr-radius;
      minHeight=Math.min(minHeight,height);maxHeight=Math.max(maxHeight,height);
      // Reuse sampled heights. Horizontal span alone cannot cover the radial
      // mismatch of coarse/fine edge polylines across a steep cliff.
      if(i>0)maxHeightStep=Math.max(maxHeightStep,Math.abs(height-fields[(gi-1)*4]*amplitude));
      if(j>0)maxHeightStep=Math.max(maxHeightStep,Math.abs(height-fields[(gi-res)*4]*amplitude));
      const f = gi * 4;
      fields[f] = (pr - radius) / amplitude; // relH
      fields[f + 1] = rivers[k];
      fields[f + 2] = temps[k];
      fields[f + 3] = moists[k];
      const kf = k * 3;
      riverFlow[o] = flows[kf];
      riverFlow[o + 1] = flows[kf + 1];
      riverFlow[o + 2] = flows[kf + 2];
      riverS[gi] = riverArcs[k];
      roadSd[gi] = roads[k];
      volcanism[gi] = volcanic[k];
      estuaryShelter[gi] = shelter[k];
      duneSand[gi] = sand[k];
      if(butte&&butte[k]>0){butteRock??=new Float32Array(vertCount);butteRock[gi]=butte[k];}
      if(canyon&&(canyon[k*2]>0||canyon[k*2+1]>0)){canyonSurface??=new Float32Array(vertCount*2);canyonSurface[gi*2]=canyon[k*2];canyonSurface[gi*2+1]=canyon[k*2+1];}
      if(inland&&inland[k*2]>0){wetlandSurface??=new Float32Array(vertCount*2);wetlandSurface[gi*2]=inland[k*2];wetlandSurface[gi*2+1]=inland[k*2+1];}
      if(snowRetention&&cold)snowRetention[gi]=cold[k];
      if(bank&&(bank[k*2]>0||bank[k*2+1]>0)){riverBankSurface??=new Float32Array(vertCount*2);riverBankSurface[gi*2]=bank[k*2];riverBankSurface[gi*2+1]=bank[k*2+1];}
      glacierIce[gi*2] = ice[k];glacierIce[gi*2+1] = glaciation[k];
      slopes[gi] = (nx * px + ny * py + nz * pz) / pr; // 1 = flat, lower = cliff
    }
  }

  // Skirt vertices: copy perimeter, sink toward planet center.
  const perim = perimeterIndices(res);
  const baseSkirtDepth = Math.max(size * radius * 0.04, 2);
  // Two observed grid steps cover sharp slope changes at mixed LOD edges.
  // Bound the extra sink by the measured patch relief; quiet ground keeps the
  // old depth. Only hidden skirts change, with no new vertices or samples.
  const skirtDepth = Math.max(baseSkirtDepth,Math.min(maxHeight-minHeight+baseSkirtDepth,2*maxHeightStep));
  const skirtBase = res * res * 3;
  for (let e = 0; e < skirtN; e++) {
    const src = perim[e] * 3;
    const ax = positions[src] + center[0];
    const ay = positions[src + 1] + center[1];
    const az = positions[src + 2] + center[2];
    const al = Math.sqrt(ax * ax + ay * ay + az * az);
    const shrink = (al - skirtDepth) / al;
    const o = skirtBase + e * 3;
    positions[o] = ax * shrink - center[0];
    positions[o + 1] = ay * shrink - center[1];
    positions[o + 2] = az * shrink - center[2];
    normals[o] = normals[src];
    normals[o + 1] = normals[src + 1];
    normals[o + 2] = normals[src + 2];
    const sf = (skirtBase / 3 + e) * 4;
    const gf = perim[e] * 4;
    fields[sf] = fields[gf];
    fields[sf + 1] = fields[gf + 1];
    fields[sf + 2] = fields[gf + 2];
    fields[sf + 3] = fields[gf + 3];
    slopes[skirtBase / 3 + e] = slopes[perim[e]];
    roadSd[skirtBase / 3 + e] = roadSd[perim[e]];
    volcanism[skirtBase / 3 + e] = volcanism[perim[e]];
    estuaryShelter[skirtBase / 3 + e] = estuaryShelter[perim[e]];
    duneSand[skirtBase / 3 + e] = duneSand[perim[e]];
    if(butteRock)butteRock[skirtBase/3+e]=butteRock[perim[e]];
    if(canyonSurface){canyonSurface[(skirtBase/3+e)*2]=canyonSurface[perim[e]*2];canyonSurface[(skirtBase/3+e)*2+1]=canyonSurface[perim[e]*2+1];}
    if(wetlandSurface){wetlandSurface[(skirtBase/3+e)*2]=wetlandSurface[perim[e]*2];wetlandSurface[(skirtBase/3+e)*2+1]=wetlandSurface[perim[e]*2+1];}
    if(snowRetention)snowRetention[skirtBase/3+e]=snowRetention[perim[e]];
    if(riverBankSurface){riverBankSurface[(skirtBase/3+e)*2]=riverBankSurface[perim[e]*2];riverBankSurface[(skirtBase/3+e)*2+1]=riverBankSurface[perim[e]*2+1];}
    glacierIce[(skirtBase / 3 + e)*2] = glacierIce[perim[e]*2];
    glacierIce[(skirtBase / 3 + e)*2+1] = glacierIce[perim[e]*2+1];
    riverS[skirtBase / 3 + e] = riverS[perim[e]];
    ppos[o] = ppos[src];
    ppos[o + 1] = ppos[src + 1];
    ppos[o + 2] = ppos[src + 2];
    riverFlow[o] = riverFlow[src];
    riverFlow[o + 1] = riverFlow[src + 1];
    riverFlow[o + 2] = riverFlow[src + 2];
  }

  // Measured AFTER the skirt is written, and read back out of the float32 buffer,
  // so the sphere bounds exactly the vertices the GPU will draw.
  const bs = boundingSphere(positions);

  const data:PatchData = {
    id: req.id, positions, normals, fields, slopes, ppos, riverFlow, riverS, roadSd, glacierIce, volcanism, estuaryShelter, duneSand, center,
    ...(butteRock?{butteRock}:{}),
    ...(canyonSurface?{canyonSurface}:{}),
    ...(wetlandSurface?{wetlandSurface}:{}),
    ...(riverBankSurface?{riverBankSurface}:{}),
    ...(snowRetention?{snowRetention}:{}),
    boundCenter: bs.center, boundRadius: bs.radius,
    ...(req.grass ? { grass: buildGrassPatch({ ...req, tuning: req.grass }, sampler) } : {}),
    ...(req.wetland ? { wetland: buildWetlandData(planet, sampler, req) } : {}),
    ...(req.coastal ? { coastal: buildCoastalData(planet, sampler, req) } : {}),
    ...(req.coastalFar ? { coastal: buildCoastalFarData(planet, sampler, req) } : {}),
    ...(req.savanna ? { savanna: buildSavannaData(planet, sampler, req) } : {}),
    ...(req.savannaFar ? { savanna: buildSavannaFarData(planet, sampler, req) } : {}),
    ...(req.riparian ? { riparian: buildRiparianData(planet,sampler,req) } : {}),
    ...(req.inlandWater ? {inlandWater:buildInlandWetlandWaterData(sampler,req)} : {}),
    ...(req.inlandWetland ? { inlandWetland: buildInlandWetlandData(planet,sampler,req) } : {}),
    ...(req.inlandWetlandFar ? { inlandWetland: buildInlandWetlandFarData(planet,sampler,req) } : {}),
  };
  if(resolveCliff&&maxHeightStep>1.5){
    let indices=cliffGridIndices.get(res);
    if(!indices){indices=buildPatchIndex(res);cliffGridIndices.set(res,indices);}
    refineWaterfallCliffMesh(data,sampler,res,indices,perim);
    if(data.indices){const bounds=boundingSphere(data.positions);data.boundCenter=bounds.center;data.boundRadius=bounds.radius;}
  }
  if(resolveWaterfallBankNormals(data,sampler,res,size*radius,face)){
    // Existing skirt tops copy their corresponding bank lighting.
    for(let i=0;i<perim.length;i++){const src=perim[i],dst=res*res+i;data.normals.set(data.normals.subarray(src*3,src*3+3),dst*3);data.slopes[dst]=data.slopes[src];}
  }
  return data;
}
